import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useEnsAvatar } from 'wagmi';
import { Link } from 'react-router';
import { useNavigation } from '../hooks/useNavigation';
import { useDomain } from '../hooks/useDomain';
import { usePagePath } from '../hooks/usePagePath';
import { useScrollContext } from '../contexts/ScrollContext';
import { useRepo } from '../hooks/useRepo';
import { useIsSnapshot } from '../hooks/useIsSnapshot';
import { DOMAIN_SUFFIX } from '../config/domain';
import Icon from './Icon';
import Notice from './Notice';
import SubscriptionNotice from './SubscriptionNotice';
import SearchModal from './SearchModal';
import RainbowEditContent from './RainbowEditContent';
import { buildFoamSvg } from '../utils/foam-icons';
import { generateEnsArtifacts } from '../utils/avatar-utils';

const Navbar = ({
  activePage,
  onNavbarInfoChange,
}) => {
  const domain = useDomain();
  const { path } = usePagePath();
  const { repo, root: repoRoot, dserviceFailed, rpcFailed } = useRepo();
  const isSnapshot = useIsSnapshot();

  const { goToView, goToEdit, goToPublish, goToSubscription, goToPages, goToFiles, goToAgents, goToDrafts, goToViewWithPreview, goToSettings, ROUTES } = useNavigation();
  const { saveScrollPosition, getScrollPosition, clearScrollPosition } = useScrollContext();

  const tabsContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [editStyle, setEditStyle] = useState(null);
  const [searchEnabled, setSearchEnabled] = useState(false);
  const avatarFromDoc = document.querySelector('link[rel="apple-touch-icon"]')?.href
  const defaultAvatarIsEns = avatarFromDoc?.includes('ensAvatar.png')
  const [avatarLogo] = useState(avatarFromDoc);
  const avatarLogoSvg = useMemo(() => (domain ? buildFoamSvg(domain, 36) : null), [domain]);
  const { data: ensAvatar } = useEnsAvatar({ name: domain, });
  // const [searchFocused, setSearchFocused] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (repo) {
        const storedEditStyle = await repo.settings.readProperty('appearance.editStyle');
        const storedForkStyle = await repo.settings.readProperty('appearance.forkStyle');
        setEditStyle(storedEditStyle || storedForkStyle || 'rainbow');
        setSearchEnabled(await repo.settings.readProperty('search.enabled') || false);
      }
    };
    loadSettings();
  }, [repo]);

  // Keyboard shortcut for search (Cmd+K on Mac, Ctrl+K on Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        if (searchEnabled && !editMode) {
          setSearchModalOpen(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchEnabled, editMode]);

  // Handle quit button click - clear scroll position and navigate to view
  const handleQuitClick = () => {
    clearScrollPosition('navbar-tabs');
    goToView();
  };

  // Check scroll position for indicators
  const checkScrollPosition = () => {
    if (tabsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const availableMenuItems = [
    {
      label: 'Settings',
      onClick: () => goToSettings(),
      icon: 'settings',
    },
    {
      label: 'Subscription',
      onClick: () => goToSubscription(),
      icon: 'credit-card',
    },
    {
      label: 'Quit editor',
      onClick: () => handleQuitClick(),
      icon: 'exit',
    },
  ]

  // Define available tabs with their navigation functions and icons
  const availableTabs = {
    'Preview': {
      onClick: () => goToViewWithPreview(path),
      icon: 'preview',
    },
    'Edit': {
      onClick: () => goToEdit(path),
      icon: 'edit',
    },
    'Pages': {
      onClick: () => goToPages(path),
      icon: 'document',
    },
    'Files': {
      onClick: () => goToFiles(path),
      icon: 'folder',
    },
    'Agents': {
      onClick: () => goToAgents(),
      icon: 'robot',
    },
    'Drafts': {
      onClick: () => goToDrafts(),
      icon: 'fork',
    },
    'Publish': {
      onClick: () => goToPublish(),
      icon: 'upload',
    }
  };

  // Check if in edit mode
  const editMode = activePage && (Object.prototype.hasOwnProperty.call(availableTabs, activePage) || availableMenuItems.find(i => i.label === activePage));

  useEffect(() => {
    const setEnsAsFavicon = async (url) => {
      if (!url) return
      const { ensFaviconBytes } = await generateEnsArtifacts(url)
      if (ensFaviconBytes) {
        const blob = new Blob([ensFaviconBytes], { type: 'image/png' })
        const iconUrl = URL.createObjectURL(blob)
        document.querySelectorAll('link[rel="icon"]').forEach(link => {
          link.href = iconUrl
        })
      }
    }
    if (ensAvatar) {
      setEnsAsFavicon(ensAvatar)
    }
  }, [ensAvatar]);

  // Set up scroll listeners for tabs
  useEffect(() => {
    if (editMode && tabsContainerRef.current) {
      const container = tabsContainerRef.current;

      // Restore scroll position if available
      const savedScrollPosition = getScrollPosition('navbar-tabs');
      if (savedScrollPosition > 0) {
        container.scrollLeft = savedScrollPosition;
      }

      checkScrollPosition();

      const handleScroll = () => {
        checkScrollPosition();
        // Save scroll position
        saveScrollPosition('navbar-tabs', container.scrollLeft);
      };

      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', checkScrollPosition);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [editMode, saveScrollPosition, getScrollPosition]);

  // Navbar info tracking (only when callback is provided)
  useEffect(() => {
    if (!onNavbarInfoChange) return;

    let navbarElement = null;
    let resizeObserver = null;
    let intersectionObserver = null;

    const updateNavbarInfo = () => {
      const navbar = document.querySelector('.navbar')?.parentElement;
      if (navbar) {
        navbarElement = navbar;

        const rect = navbar.getBoundingClientRect();
        const effectiveTop = Math.max(0, rect.bottom);

        onNavbarInfoChange(effectiveTop);
      }
    };

    const setupObservers = () => {
      if (!navbarElement) return;

      // ResizeObserver for height changes
      resizeObserver = new ResizeObserver(() => {
        updateNavbarInfo();
      });
      resizeObserver.observe(navbarElement);

      // IntersectionObserver for visibility tracking
      intersectionObserver = new IntersectionObserver(
        () => {
          updateNavbarInfo();
        },
        {
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
          rootMargin: '0px'
        }
      );
      intersectionObserver.observe(navbarElement);
    };

    // Initial setup
    updateNavbarInfo();
    
    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(setupObservers, 100);

    // Listen for window resize and scroll
    window.addEventListener('resize', updateNavbarInfo);
    window.addEventListener('scroll', updateNavbarInfo);
    window.addEventListener('overrides-banner-visibility-change', updateNavbarInfo);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateNavbarInfo);
      window.removeEventListener('scroll', updateNavbarInfo);
      window.removeEventListener('overrides-banner-visibility-change', updateNavbarInfo);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
  }, [onNavbarInfoChange]);

  const editButton = editStyle && (
    <div className="tooltip tooltip-bottom" data-tip={editStyle === 'plain' ? 'Edit' : ''}>
      <button
        className={`btn btn-sm ${editStyle === 'rainbow' ? 'btn-ghost rainbow-edit' : 'bg-transparent'} text-lg`}
        aria-label="Edit"
        onClick={() => { goToEdit(path) }}
      >
        {editStyle === 'rainbow' ? (
          <RainbowEditContent />
        ) : (
          <Icon name="edit" size={4} />
        )}
      </button>
    </div>
  )
  const rootHref = window.location.origin;
  const domainTitle = (
    <a className="text-base font-bold" href={rootHref}>
      {domain}
    </a>
  )
  const showSearch = searchEnabled && !editMode

  return (<>
    {isSnapshot && (
      <Notice type="info" className="z-50">
        <strong>Website Snapshot.</strong> You are looking at an old version of this website. <a className="link" href={`https://${domain}${DOMAIN_SUFFIX}`} target="_blank" rel="noopener noreferrer">View latest version.</a>
      </Notice>
    )}
    {dserviceFailed && !isSnapshot && (
      <Notice type="warning" className="z-50">
        <strong>Failed to connect to DService.</strong> If you have access to a DService endpoint, you can set it in the URL as <code>?ds-new.simplepage.eth=your-dservice.com</code>
      </Notice>
    )}
    {rpcFailed && (
      <Notice type="warning" className="z-50">
        <strong>Failed to connect to RPC.</strong> If you have access to a ethereum RPC endpoint, you can set it in the URL as <code>?ds-rpc-1=your-rpc.com</code>
      </Notice>
    )}
    {activePage !== 'Subscription' && (
      <SubscriptionNotice editMode={editMode} />
    )}
    <SearchModal 
      isOpen={searchModalOpen} 
      onClose={() => setSearchModalOpen(false)} 
    />
    <div className="relative z-[100] border-b bg-base-100 border-base-300">
      <div className="navbar z-[100] relative">
        <div className="navbar-start ml-2">
          <a href={rootHref} className="inline-flex">
            {defaultAvatarIsEns || ensAvatar ? (
              <img
                src={ensAvatar || avatarLogo}
                alt="Logo"
                className="h-9 w-9 mask mask-squircle"
              />
            ) : (
              <span
                className="h-9 w-9 mask mask-squircle inline-flex"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: avatarLogoSvg }}
              />
            )}
          </a>
        </div>
        <div className="navbar-center flex items-center justify-center h-full">
          {domainTitle}
          {repoRoot && (
            <Link
              data-tip="Version history"
              className="tooltip tooltip-bottom text-base cursor-pointer pl-1 flex items-center leading-none"
              to={ROUTES.HISTORY}
            >
              <Icon name="proof" size={4} />
            </Link>
          )}
        </div>
        <div className="navbar-end mr-4">
          <div className="flex gap-2 items-center">
            {/* Search bar */}
            {showSearch && (
              <>
                {/* Desktop search input - hidden on mobile */}
                <div className="relative hidden sm:block">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Icon name="search" size={4} className="text-base-content/60" />
                    </div>
                    <input 
                      ref={searchInputRef}
                      type="text" 
                      placeholder="Search" 
                      className={`input input-sm pl-10 pr-16 cursor-pointer w-36`}
                      onClick={() => setSearchModalOpen(true)}
                      readOnly
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <kbd className="kbd kbd-xs text-base-content/60">
                        {navigator.platform.toLowerCase().includes('mac') ? '⌘ ' : 'Ctrl '}K
                      </kbd>
                    </div>
                  </div>
                </div>
                
                {/* Mobile search button - visible only on mobile */}
                <div className="block sm:hidden">
                  <button
                    className="btn btn-sm bg-transparent ml-2"
                    onClick={() => setSearchModalOpen(true)}
                    title="Search"
                  >
                    <Icon name="search" size={4} />
                  </button>
                </div>
              </>
            )}


            {/* Edit button */}
            {!editMode ? editButton : (<>
                {/* Hamburger menu */}
                <div className="dropdown dropdown-end relative z-[100]">
                  <div tabIndex={0} role="button" className="btn btn-ghost btn-sm" >
                    <Icon name="hamburger" size={5} />
                  </div>
                  <ul
                    tabIndex={0}
                    className="menu menu-sm dropdown-content bg-base-100 rounded-box z-[100] mt-3 p-2 shadow absolute w-max"
                  >
                    {availableMenuItems.map((item) => (
                      <li key={item.label}>
                        <a onClick={item.onClick}>
                          <Icon name={item.icon} />
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
            </>)}
          </div>
        </div>
      </div>
      {editMode && (
        <div className="relative">
          {/* Left scroll indicator */}
          {canScrollLeft && (
            <div className="pointer-events-none absolute left-1 top-1/2 -translate-y-1/2 z-10 text-base-content/60 text-sm">
              ⟨
            </div>
          )}
          <div className="overflow-x-auto" ref={tabsContainerRef}>
            <div className="tabs tabs-border min-w-max">
              {Object.keys(availableTabs).map((tab) => (
                <a
                  key={tab}
                  role="tab"
                  className={`tab group ${activePage === tab ? 'tab-active' : ''}`}
                  onClick={() => availableTabs[tab].onClick()}
                >
                  <span className="mr-1">
                    <Icon name={availableTabs[tab].icon} className={`opacity-${activePage === tab ? '100' : '50'} group-hover:opacity-100`} />
                  </span>
                  {tab}
                </a>
              ))}
            </div>
          </div>
          {/* Right scroll indicator */}
          {canScrollRight && (
            <div className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 z-10 text-base-content/60 text-sm">
              ⟩
            </div>
          )}
        </div>
      )}
    </div>
  </>);
};

export default Navbar;
